import React from "react";
import {
  Box,
  Typography,
  Divider,
  Card,
  CardContent,
  Grid,
  LinearProgress,
} from "@mui/material";
import {
  Analytics as AnalyticsIcon,
  Message as MessageIcon,
  Person as PersonIcon,
  SmartToy as AIIcon,
  Timeline as TimelineIcon,
} from "@mui/icons-material";
import type { BossBabyChatSessionDto } from "@/apis/bossBabyChatLog";

interface StatisticsTabProps {
  session: BossBabyChatSessionDto;
}

/**
 * Boss Baby 통계 정보 탭 컴포넌트
 *
 * @description Boss Baby 세션의 통계 정보를 표시하는 탭
 * @author Max <sud665@ymjlab.com>
 * @version 1.0.0
 * @since 2025-01-29
 */
export const StatisticsTab: React.FC<StatisticsTabProps> = ({ session }) => {
  const messages = session.messages || [];
  
  // 메시지 통계 계산
  const userMessages = messages.filter(msg => msg.sender === 'user');
  const aiMessages = messages.filter(msg => msg.sender === 'ai');
  const totalMessages = messages.length;
  
  const userMessageRatio = totalMessages > 0 ? (userMessages.length / totalMessages) * 100 : 0;
  const aiMessageRatio = totalMessages > 0 ? (aiMessages.length / totalMessages) * 100 : 0;

  // 세션 지속 시간 계산 (분 단위)
  const sessionDuration = Math.floor(
    (new Date(session.lastActivity).getTime() - new Date(session.regDttm).getTime()) / (1000 * 60)
  );

  // 메시지 타입별 통계
  const messageTypes = messages.reduce((acc, msg) => {
    acc[msg.messageType] = (acc[msg.messageType] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Session Statistics
      </Typography>
      <Divider sx={{ mb: 3 }} />

      <Grid container spacing={3}>
        {/* 메시지 통계 */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <MessageIcon sx={{ mr: 1, color: "primary.main" }} />
                <Typography variant="h6">Message Statistics</Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: "center", p: 2 }}>
                    <Typography variant="h3" color="primary.main" fontWeight="bold">
                      {totalMessages}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Messages
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={6}>
                  <Box sx={{ textAlign: "center", p: 2 }}>
                    <Typography variant="h3" color="info.main" fontWeight="bold">
                      {sessionDuration}m
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Session Duration
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={6}>
                  <Box sx={{ textAlign: "center", p: 2 }}>
                    <Typography variant="h3" color="success.main" fontWeight="bold">
                      {userMessages.length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      User Messages
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={6}>
                  <Box sx={{ textAlign: "center", p: 2 }}>
                    <Typography variant="h3" color="warning.main" fontWeight="bold">
                      {aiMessages.length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      AI Messages
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* 메시지 비율 */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <AnalyticsIcon sx={{ mr: 1, color: "primary.main" }} />
                <Typography variant="h6">Message Distribution</Typography>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <PersonIcon sx={{ mr: 1, color: "success.main" }} />
                  <Typography variant="body2">
                    User Messages ({userMessageRatio.toFixed(1)}%)
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={userMessageRatio}
                  color="success"
                  sx={{ height: 8, borderRadius: 4, mb: 2 }}
                />

                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <AIIcon sx={{ mr: 1, color: "warning.main" }} />
                  <Typography variant="body2">
                    AI Messages ({aiMessageRatio.toFixed(1)}%)
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={aiMessageRatio}
                  color="warning"
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>

              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Average messages per minute: {sessionDuration > 0 ? (totalMessages / sessionDuration).toFixed(1) : 'N/A'}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* 메시지 타입 분석 */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <TimelineIcon sx={{ mr: 1, color: "primary.main" }} />
                <Typography variant="h6">Message Type Analysis</Typography>
              </Box>

              <Grid container spacing={2}>
                {Object.entries(messageTypes).map(([type, count]) => (
                  <Grid item xs={12} sm={6} md={3} key={type}>
                    <Box sx={{ textAlign: "center", p: 2, border: 1, borderColor: "divider", borderRadius: 2 }}>
                      <Typography variant="h4" color="primary.main" fontWeight="bold">
                        {count}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {type.toUpperCase()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ({totalMessages > 0 ? ((count / totalMessages) * 100).toFixed(1) : 0}%)
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              {Object.keys(messageTypes).length === 0 && (
                <Box sx={{ textAlign: "center", py: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    No message type data available
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* 시간 분석 */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <TimelineIcon sx={{ mr: 1, color: "primary.main" }} />
                <Typography variant="h6">Time Analysis</Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: "center", p: 2 }}>
                    <Typography variant="h4" color="primary.main" fontWeight="bold">
                      {new Date(session.regDttm).toLocaleDateString("en-US")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Session Date
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: "center", p: 2 }}>
                    <Typography variant="h4" color="success.main" fontWeight="bold">
                      {new Date(session.regDttm).toLocaleTimeString("en-US", { 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Start Time
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: "center", p: 2 }}>
                    <Typography variant="h4" color="warning.main" fontWeight="bold">
                      {new Date(session.lastActivity).toLocaleTimeString("en-US", { 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Last Activity
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: "center", p: 2 }}>
                    <Typography variant="h4" color="info.main" fontWeight="bold">
                      {sessionDuration}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Duration (minutes)
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};